import React, { useMemo } from "react";
import { Box, Grid, Stack, Typography, CircularProgress } from "@mui/material";
import moment from "moment";
import { shiftPalette, classifyShift, formatShiftClock } from "./shiftHelpers";
import { designWeights } from "services/constants/designTokens";

const WeekDays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Build a Mon-anchored date-range grid (weeks x 7), padding leading/trailing
// days around the selected range (flagged `outside` so they render greyed).
const buildWeeks = (monthlyData, startDate, endDate) => {
  const rows = (monthlyData || []).flat();
  const result = [];
  let week = [];

  const rangeStart = moment(startDate, ["YYYY-MM-DD", "DD-MM-YYYY"], true).startOf("day");
  const rangeEnd = moment(endDate, ["YYYY-MM-DD", "DD-MM-YYYY"], true).startOf("day");

  if (!rangeStart.isValid() || !rangeEnd.isValid() || rangeEnd.isBefore(rangeStart, "day")) {
    return result;
  }

  const parsedRows = rows.map((row) => ({
    ...row,
    parsedDate: moment(row.date, ["DD-MM-YYYY", "YYYY-MM-DD", moment.ISO_8601], true),
  }));
  const findDay = (d) =>
    parsedRows.find((row) => row.parsedDate.isValid() && row.parsedDate.isSame(d, "day"));

  const currentDate = rangeStart.clone().startOf("isoWeek");
  const gridEnd = rangeEnd.clone().endOf("isoWeek");

  while (currentDate.isSameOrBefore(gridEnd, "day")) {
    const outside =
      currentDate.isBefore(rangeStart, "day") || currentDate.isAfter(rangeEnd, "day");
    const dayData = outside ? null : findDay(currentDate);
    week.push({
      day_number: currentDate.date(),
      shift_name: dayData?.shift_name || "",
      start_time: dayData?.shift_start_time || "",
      end_time: dayData?.shift_end_time || "",
      is_weekoff: dayData?.is_weekoff,
      outside,
    });

    if (week.length === 7) {
      result.push(week);
      week = [];
    }
    currentDate.add(1, "day");
  }
  return result;
};

const DayCell = ({ item }) => {
  const category = item.outside
    ? null
    : classifyShift(item.shift_name, item.is_weekoff);
  const hasTime = item.start_time && item.end_time;

  return (
    <Box
      sx={{
        minHeight: 96,
        p: 1,
        border: `1px solid ${shiftPalette.border}`,
        bgcolor: item.outside ? "#FAFBFC" : "#fff",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: designWeights.medium,
          color: item.outside ? "#C7CDD6" : shiftPalette.text,
          mb: 0.5,
        }}
      >
        {item.day_number}
      </Typography>

      {item.outside ? (
        <Typography variant="caption" sx={{ color: "#C7CDD6", textAlign: "center", mt: 1 }}>
          -
        </Typography>
      ) : category ? (
        <Box
          sx={{
            flex: 1,
            borderRadius: 1.5,
            p: 0.75,
            bgcolor: `${category.color}14`,
            border: `1px solid ${category.color}33`,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Stack direction="row" spacing={0.5} alignItems="center">
            <category.icon sx={{ fontSize: 14, color: category.color }} />
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: designWeights.semibold,
                color: category.color,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {/* Real assigned shift name, not the generic category
              bucket label -- see Weekly.js's identical fix. */}
              {item.shift_name || category.label}
            </Typography>
          </Stack>
          <Typography sx={{ fontSize: 10.5, color: shiftPalette.subText, mt: 0.25 }}>
            {hasTime
              ? `${formatShiftClock(item.start_time)} - ${formatShiftClock(item.end_time)}`
              : "-"}
          </Typography>
        </Box>
      ) : (
        <Typography variant="caption" sx={{ color: "#C7CDD6", textAlign: "center", mt: 1 }}>
          -
        </Typography>
      )}
    </Box>
  );
};

export const Monthly = ({ loading, monthlyData, startDate, endDate }) => {
  const weeks = useMemo(
    () => (loading ? [] : buildWeeks(monthlyData, startDate, endDate)),
    [loading, monthlyData, startDate, endDate],
  );

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box sx={{ overflowX: "auto", pb: 0.5 }}>
      <Box sx={{ minWidth: { xs: 720, md: "auto" } }}>
      {/* Weekday header */}
      <Grid container>
        {WeekDays.map((day) => (
          <Grid
            key={day}
            item
            xs={12 / 7}
            sx={{
              py: 1,
              border: `1px solid ${shiftPalette.border}`,
              bgcolor: "#F7F8FA",
              textAlign: "center",
            }}
          >
            <Typography sx={{ fontSize: 13, fontWeight: designWeights.semibold, color: shiftPalette.subText }}>
              {day}
            </Typography>
          </Grid>
        ))}
      </Grid>

      {/* Weeks */}
      {weeks.map((week, wIdx) => (
        <Grid container key={wIdx}>
          {week.map((item, dIdx) => (
            <Grid item xs={12 / 7} key={dIdx}>
              <DayCell item={item} />
            </Grid>
          ))}
        </Grid>
      ))}
      </Box>
    </Box>
  );
};

